import React, { useEffect } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { GlobalStyles } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import DashboardUser from "./components/DashboardUser";
import DashboardAdmin from "./components/DashboardAdmin";
import Login from "./components/Login";
import Landing from "./components/Landing";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";
import Download from "./components/Download";
import ForgotPassword from "./components/ForgotPassword";
import Users from "./components/Users";
import LocalsList from "./components/Locals/List";
import ActivityList from "./components/Activity/List";
import AlertList from "./components/Alerts/List";
import ConnectionList from "./components/Connections/List";
import AccountList from "./components/Accounts/List";
import Feedbacks from "./components/Feedbacks";
import Protected from "./components/Protected";
import CompanyList from "./components/Company/List";
import Strategy from "./components/Strategy";
import NotificationList from "./components/Notification/List";
import AuditList from "./components/Audit/List";
import RatingList from "./components/Rating/List";
import VersionList from "./components/Versions/List";
import { useSelector, useDispatch } from "react-redux";
import { validateAuthAction } from "./redux/actions/login";
import "./styles/global.css";

const defaultTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b95e8',
    },
    secondary: {
      main: '#3b95e8',
    },
    background: {
      default: '#0a1628',
      paper: '#0d1b36',
    },
    text: {
      primary: '#e8f0fe',
      secondary: 'rgba(232,240,254,0.55)',
    },
    warning: {
      main: '#E2925D',
    },
    divider: 'rgba(255,255,255,0.08)',
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  components: {
    MuiInputBase: {
      defaultProps: {
        disableInjectingGlobalStyles: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'rgba(255,255,255,0.04)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.09)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.18)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3b95e8',
            borderWidth: 1,
          },
        },
        input: {
          color: '#e8f0fe',
          fontSize: 14,
          padding: '12px 14px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(232,240,254,0.38)',
          fontSize: 14,
          '&.Mui-focused': {
            color: '#3b95e8',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: 'rgba(232,240,254,0.35)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          color: '#e8f0fe',
          '&.Mui-selected': {
            backgroundColor: 'rgba(59,149,232,0.12)',
            '&:hover': { backgroundColor: 'rgba(59,149,232,0.18)' },
          },
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(59,149,232,0.15)',
            '& .MuiListItemIcon-root': {
              color: '#3b95e8',
            },
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(59,149,232,0.22)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, #0a1628 0%, #0d1b36 100%)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'transparent',
          boxShadow: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255,255,255,0.08)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(255,255,255,0.08)',
        },
      },
    },
  },
});

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.login.tokenAccess);
  const user = useSelector((state) => state.user.user);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);

  useEffect(() => {
    dispatch(validateAuthAction());
  }, [dispatch]);

  return (
    <div className="App">
      <ThemeProvider theme={defaultTheme}>
        <GlobalStyles
          styles={{
            "@keyframes mui-auto-fill": { from: { display: "block" } },
            "@keyframes mui-auto-fill-cancel": { from: { display: "block" } },
          }}
        />
        <CssBaseline />
        <Routes>
          {/* Always public */}
          <Route path="/terms/:lang?/:mode?" element={<Terms />} />
          <Route path="/privacy/:lang?/:mode?" element={<Privacy />} />
          <Route path="/download" element={<Download />} />

          {isAuthenticated ? (
            <>
              <Route path="/" element={<Protected />}>
                <Route
                  index
                  element={
                    user.admin && !selectedCompany ? (
                      <DashboardAdmin />
                    ) : (
                      <DashboardUser />
                    )
                  }
                />
                {user.admin && (
                  <>
                    <Route path="/companies" element={<CompanyList />} />
                    <Route path="/audit" element={<AuditList />} />
                    <Route path="/ratings" element={<RatingList />} />
                    <Route path="/versions" element={<VersionList />} />
                    <Route path="/connections" element={<ConnectionList />} />
                    <Route path="/accounts" element={<AccountList />} />
                  </>
                )}
                <>
                  <Route path="/users" element={<Users />} />
                  <Route path="/locals" element={<LocalsList />} />
                  <Route path="/feedbacks" element={<Feedbacks />} />
                  <Route path="/notifications" element={<NotificationList />} />
                  <Route path="/strategy" element={<Strategy />} />
                  <Route path="/activity" element={<ActivityList />} />
                  <Route path="/alerts" element={<AlertList />} />
                </>
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
            </>
          ) : (
            <>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot" element={<ForgotPassword />} />
              <Route path="/forgot/:token" element={<ForgotPassword />} />
              <Route path="*" element={<Navigate to="/" />} />
            </>
          )}
        </Routes>
      </ThemeProvider>
    </div>
  );
}

export default App;
